import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, TrendingUp, TrendingDown, Wallet, Banknote,
    FileText, Upload, Trash2, FileImage, FileType2,
    Wrench, ClipboardCheck, Plus, Pencil, ClipboardList, Gauge, Camera, X,
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
    PieChart, Pie, Cell,
} from "recharts";
import { api, API, fmtMXN, fmtDate, daysAgoISO, todayISO, withToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CarQrButton } from "../components/CarQrButton";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const DOC_TYPES = [
    "Tarjeta de circulación",
    "Factura",
    "Verificación",
    "Tenencia",
    "Permiso / Concesión",
    "Licencia del conductor",
    "Comprobante de domicilio",
    "Otro",
];

export default function CarDetail() {
    const { carId } = useParams();
    const { isAdmin, canWrite } = useAuth();
    const [car, setCar] = useState(null);
    const [summary, setSummary] = useState(null);
    const [txs, setTxs] = useState([]);
    const [insurance, setInsurance] = useState([]);
    const [verifications, setVerifications] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [checklistTemplate, setChecklistTemplate] = useState([]);
    const [serviceLogs, setServiceLogs] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [startDate, setStartDate] = useState(daysAgoISO(90));
    const [endDate, setEndDate] = useState(todayISO());
    const [editValueOpen, setEditValueOpen] = useState(false);
    const [valueInput, setValueInput] = useState("");

    const loadCar = useCallback(() => api.get("/cars").then((r) => setCar(r.data.find((x) => x.id === carId))), [carId]);
    const loadDocs = useCallback(() => api.get(`/cars/${carId}/documents`).then((r) => setDocuments(r.data)), [carId]);
    const loadVerifs = useCallback(() => api.get("/verifications", { params: { car_id: carId } }).then((r) => setVerifications(r.data)), [carId]);
    const loadChecklists = useCallback(() => api.get(`/cars/${carId}/checklists`).then((r) => setChecklists(r.data)), [carId]);
    const loadServiceLogs = useCallback(() => api.get(`/cars/${carId}/service-logs`).then((r) => setServiceLogs(r.data)).catch(() => toast.error("No se pudo cargar la bitácora de servicios")), [carId]);

    useEffect(() => {
        loadCar();
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/suppliers").then((r) => setSuppliers(r.data));
        api.get("/insurance", { params: { car_id: carId } }).then((r) => setInsurance(r.data));
        api.get("/checklist-template").then((r) => setChecklistTemplate(r.data));
        api.get("/service-types").then((r) => setServiceTypes(r.data));
        loadDocs();
        loadVerifs();
        loadChecklists();
        loadServiceLogs();
    }, [carId, loadCar, loadDocs, loadVerifs, loadChecklists, loadServiceLogs]);

    useEffect(() => {
        const params = { car_id: carId, start_date: startDate, end_date: endDate };
        api.get("/analytics/summary", { params }).then((r) => setSummary(r.data));
        api.get("/transactions", { params }).then((r) => setTxs(r.data));
    }, [carId, startDate, endDate]);

    const COLORS = ["#0F172A", "#475569", "#94A3B8", "#CBD5E1", "#E11D48", "#F43F5E"];
    const expenseByCat = (summary?.by_category || []).filter((c) => c.type === "expense").sort((a,b)=>b.total-a.total);

    const openEditValue = () => { setValueInput(car?.value ?? ""); setEditValueOpen(true); };
    const saveCarValue = async () => {
        try {
            await api.put(`/cars/${carId}`, {
                plate: car.plate, brand: car.brand, model: car.model, year: car.year,
                color: car.color, driver_name: car.driver_name, image_url: car.image_url,
                notes: car.notes, value: valueInput !== "" ? Number(valueInput) : null,
            });
            toast.success("Valor del auto actualizado");
            setEditValueOpen(false);
            loadCar();
        } catch {
            toast.error("Error al actualizar el valor");
        }
    };

    if (!car) return <div className="py-20 text-center text-slate-400">Cargando…</div>;

    return (
        <div className="space-y-6" data-testid="car-detail-page">
            <div>
                <Link to="/autos" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
                    <ArrowLeft size={14} className="mr-1" /> Volver a autos
                </Link>
            </div>

            <header className="flex flex-col md:flex-row md:items-center gap-4">
                {car.image_url && (
                    <div className="w-full md:w-48 h-32 rounded-lg overflow-hidden bg-slate-100">
                        <img src={car.image_url} alt={car.plate} className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="flex-1">
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{car.plate}</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">{car.brand} {car.model}</h1>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 mt-2">
                        {car.year && <Badge variant="secondary">{car.year}</Badge>}
                        {car.color && <Badge variant="secondary">{car.color}</Badge>}
                        {car.driver_name && <Badge variant="secondary">Conductor: {car.driver_name}</Badge>}
                        {car.value != null && (
                            <Badge className="bg-slate-900 text-white hover:bg-slate-900">
                                <Banknote size={12} className="mr-1" /> Valor: {fmtMXN(car.value)}
                            </Badge>
                        )}
                    </div>
                    {car.notes && <p className="text-sm text-slate-500 mt-2">{car.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <CarQrButton car={car} variant="button" />
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-white" />
                    <span className="text-slate-400">→</span>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-white" />
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Kpi label="Ingresos" value={fmtMXN(summary?.total_income || 0)} icon={<TrendingUp className="text-emerald-600" size={20} />} accent="emerald" />
                <Kpi label="Gastos" value={fmtMXN(summary?.total_expense || 0)} icon={<TrendingDown className="text-rose-600" size={20} />} accent="rose" />
                <Kpi label="Neto" value={fmtMXN(summary?.net || 0)} icon={<Wallet className="text-slate-700" size={20} />} accent={(summary?.net||0)>=0?"emerald":"rose"} />
                <Kpi
                    label="Valor del auto"
                    value={car.value != null ? fmtMXN(car.value) : "—"}
                    icon={<Banknote className="text-slate-700" size={20} />}
                    accent="slate"
                    onEdit={canWrite ? openEditValue : undefined}
                />
            </div>

            <Dialog open={editValueOpen} onOpenChange={setEditValueOpen}>
                <DialogContent className="max-w-sm" data-testid="edit-car-value-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Actualizar valor del auto</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label className="text-xs">Valor del auto (MXN)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={valueInput}
                            onChange={(e) => setValueInput(e.target.value)}
                            placeholder="Ej. 285000.00"
                            data-testid="car-value-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditValueOpen(false)}>Cancelar</Button>
                        <Button onClick={saveCarValue} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-car-value">
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="border-slate-200 shadow-sm lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Tendencia</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={summary?.timeline || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
                                    <Tooltip formatter={(v) => fmtMXN(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="income" name="Ingresos" stroke="#10B981" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="expense" name="Gastos" stroke="#E11D48" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Gastos por categoría</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {expenseByCat.length === 0 ? (
                            <div className="h-56 grid place-items-center text-sm text-slate-400">Sin datos</div>
                        ) : (
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={expenseByCat} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                                            {expenseByCat.map((entry, i) => <Cell key={`${entry.category_id}-${entry.type}`} fill={COLORS[i % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmtMXN(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <ExpedienteSection carId={carId} documents={documents} reload={loadDocs} isAdmin={isAdmin} canWrite={canWrite} />

            <ChecklistSection
                carId={carId}
                items={checklists}
                template={checklistTemplate}
                reload={loadChecklists}
                isAdmin={isAdmin}
                canWrite={canWrite}
            />

            <ServiceLogsSection
                carId={carId}
                items={serviceLogs}
                suppliers={suppliers}
                serviceTypes={serviceTypes}
                reload={loadServiceLogs}
                isAdmin={isAdmin}
                canWrite={canWrite}
            />

            <VerificationsSection carId={carId} items={verifications} reload={loadVerifs} isAdmin={isAdmin} canWrite={canWrite} />

            {insurance.length > 0 && (
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg font-display font-medium">Seguros y administrativo</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {insurance.map((ins) => (
                            <div key={ins.id} className="flex items-center justify-between p-3 rounded-md border border-slate-200" data-testid={`insurance-row-${ins.id}`}>
                                <div>
                                    <div className="font-medium text-slate-900">{ins.provider} · {ins.coverage_type || "Cobertura"}</div>
                                    <div className="text-xs text-slate-500 mt-1">Póliza {ins.policy_number || "—"} · vigencia {fmtDate(ins.start_date)} → {fmtDate(ins.end_date)}</div>
                                </div>
                                <Badge className="bg-slate-100 text-slate-800 hover:bg-slate-100">{fmtMXN(ins.cost || 0)}</Badge>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium">Movimientos del periodo</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                    <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                                    <th className="text-left px-4 py-3 font-semibold">Categoría</th>
                                    <th className="text-left px-4 py-3 font-semibold">Proveedor</th>
                                    <th className="text-right px-4 py-3 font-semibold">Piezas</th>
                                    <th className="text-right px-4 py-3 font-semibold">Monto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {txs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Sin movimientos</td></tr>}
                                {txs.map((t) => {
                                    const cat = categories.find((c) => c.id === t.category_id);
                                    const sup = suppliers.find((s) => s.id === t.supplier_id);
                                    return (
                                        <tr key={t.id} className="border-t border-slate-100">
                                            <td className="px-4 py-3 text-slate-700">{fmtDate(t.date)}</td>
                                            <td className="px-4 py-3">
                                                {t.type === "income" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ingreso</Badge> : <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Gasto</Badge>}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">{cat?.name || "—"}</td>
                                            <td className="px-4 py-3 text-slate-700">{sup?.name || "—"}</td>
                                            <td className="px-4 py-3 text-right">{t.piece_count || 1}</td>
                                            <td className={`px-4 py-3 text-right font-semibold ${t.type==="income"?"text-emerald-600":"text-rose-600"}`}>{fmtMXN(t.amount)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function ExpedienteSection({ carId, documents, reload, isAdmin, canWrite }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [docType, setDocType] = useState("Otro");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const reset = () => { setTitle(""); setDocType("Otro"); setFile(null); if (inputRef.current) inputRef.current.value = ""; };

    const submit = async () => {
        if (!file) return toast.error("Selecciona un archivo");
        const fd = new FormData();
        fd.append("file", file);
        const params = new URLSearchParams();
        if (title) params.append("title", title);
        if (docType) params.append("doc_type", docType);
        setUploading(true);
        try {
            await api.post(`/cars/${carId}/documents?${params.toString()}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Documento agregado");
            reset();
            setOpen(false);
            reload();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Error al subir";
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`¿Eliminar "${doc.title}" del expediente?`)) return;
        await api.delete(`/cars/${carId}/documents/${doc.id}`);
        toast.success("Eliminado");
        reload();
    };

    return (
        <Card className="border-slate-200 shadow-sm" data-testid="expediente-section">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <FileText size={18} className="text-slate-500" />
                        Expediente del auto
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Sube tarjeta de circulación, factura, verificación y otros documentos (PDF/JPG/PNG, máx. 10 MB).</p>
                </div>
                {canWrite && (
                    <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-document-button">
                        <Upload size={16} className="mr-1" /> Agregar documento
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {documents.length === 0 ? (
                    <div className="py-10 text-center text-slate-400 text-sm">Aún no hay documentos en el expediente.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {documents.map((d) => {
                            const isPdf = d.content_type === "application/pdf";
                            const url = withToken(`${API}/files/${d.storage_path}`);
                            return (
                                <a
                                    key={d.id}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:border-slate-400 transition-colors bg-white"
                                    data-testid={`doc-${d.id}`}
                                >
                                    <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-slate-500 shrink-0 overflow-hidden">
                                        {isPdf ? (
                                            <FileType2 size={22} />
                                        ) : (
                                            <img src={url} alt={d.title} className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-slate-900 truncate">{d.title}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {d.doc_type ? `${d.doc_type} · ` : ""}{fmtDate(d.uploaded_at)}
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                                            {isPdf ? "PDF" : <span className="inline-flex items-center gap-1"><FileImage size={10} /> Imagen</span>} · {(d.size/1024).toFixed(0)} KB
                                        </div>
                                    </div>
                                    {isAdmin && (
                                        <button
                                            onClick={(e) => { e.preventDefault(); remove(d); }}
                                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600 opacity-60 group-hover:opacity-100"
                                            data-testid={`del-doc-${d.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </a>
                            );
                        })}
                    </div>
                )}
            </CardContent>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogContent data-testid="document-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Agregar al expediente</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Tipo de documento</Label>
                            <select
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                className="w-full mt-1 bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 focus:outline-none text-slate-900"
                                data-testid="doc-type"
                            >
                                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Título (opcional)</Label>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Factura original" data-testid="doc-title" />
                        </div>
                        <div>
                            <Label className="text-xs">Archivo (PDF, JPG, PNG, WEBP)</Label>
                            <input
                                ref={inputRef}
                                type="file"
                                accept="application/pdf,image/jpeg,image/png,image/webp"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                                data-testid="doc-file"
                            />
                            {file && <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size/1024).toFixed(0)} KB</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={uploading} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-document">
                            {uploading ? "Subiendo…" : "Subir"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function VerificationsSection({ carId, items, reload, isAdmin, canWrite }) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const today = new Date();
    const empty = {
        period: "",
        verification_date: todayISO(),
        next_due_date: daysAgoISO(-180),
        result: "aprobada",
        sticker: "",
        cost: "",
        notes: "",
    };
    const [form, setForm] = useState(empty);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (v) => {
        setEditing(v);
        setForm({
            period: v.period || "",
            verification_date: v.verification_date,
            next_due_date: v.next_due_date,
            result: v.result || "aprobada",
            sticker: v.sticker || "",
            cost: v.cost ?? "",
            notes: v.notes || "",
        });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.verification_date || !form.next_due_date) return toast.error("Fechas requeridas");
        const payload = {
            car_id: carId,
            period: form.period || null,
            verification_date: form.verification_date,
            next_due_date: form.next_due_date,
            result: form.result || null,
            sticker: form.sticker || null,
            cost: form.cost !== "" ? Number(form.cost) : null,
            notes: form.notes || null,
        };
        try {
            if (editing) await api.put(`/verifications/${editing.id}`, payload);
            else await api.post("/verifications", payload);
            toast.success("Guardado");
            setOpen(false); reload();
        } catch { toast.error("Error"); }
    };
    const remove = async (v) => {
        if (!window.confirm("¿Eliminar registro de verificación?")) return;
        await api.delete(`/verifications/${v.id}`);
        toast.success("Eliminado");
        reload();
    };

    const sorted = [...items].sort((a, b) => b.next_due_date.localeCompare(a.next_due_date));
    const next = sorted[0];
    const nextStatus = (() => {
        if (!next) return null;
        const d = new Date(next.next_due_date);
        const days = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
        if (days < 0) return { label: `Vencida hace ${Math.abs(days)} d`, cls: "bg-rose-100 text-rose-700" };
        if (days <= 30) return { label: `Vence en ${days} d`, cls: "bg-amber-100 text-amber-700" };
        return { label: `Vigente · ${days} d`, cls: "bg-emerald-100 text-emerald-700" };
    })();

    const resultBadge = (r) => {
        const map = {
            aprobada: "bg-emerald-100 text-emerald-700",
            rechazada: "bg-rose-100 text-rose-700",
            exenta: "bg-slate-100 text-slate-700",
            pendiente: "bg-amber-100 text-amber-700",
        };
        const cls = map[r] || "bg-slate-100 text-slate-700";
        return <Badge className={`${cls} hover:${cls} capitalize`}>{r || "—"}</Badge>;
    };

    return (
        <Card className="border-slate-200 shadow-sm" data-testid="verifications-section">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <ClipboardCheck size={18} className="text-slate-500" />
                        Verificación vehicular
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Historial de verificaciones y próxima fecha de vencimiento.</p>
                </div>
                <div className="flex items-center gap-2">
                    {nextStatus && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Próxima</div>
                            <div className="text-sm font-medium text-slate-900">{fmtDate(next.next_due_date)}</div>
                            <Badge className={`${nextStatus.cls} hover:${nextStatus.cls} mt-1`}>{nextStatus.label}</Badge>
                        </div>
                    )}
                    {canWrite && (
                        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-verification-button">
                            <Plus size={16} className="mr-1" /> Registrar
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {items.length === 0 ? (
                    <div className="px-6 py-10 text-center text-slate-400 text-sm">Aún no hay verificaciones registradas.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Periodo</th>
                                    <th className="text-left px-4 py-3 font-semibold">Realizada</th>
                                    <th className="text-left px-4 py-3 font-semibold">Próximo venc.</th>
                                    <th className="text-left px-4 py-3 font-semibold">Resultado</th>
                                    <th className="text-left px-4 py-3 font-semibold">Holograma</th>
                                    <th className="text-right px-4 py-3 font-semibold">Costo</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((v) => (
                                    <tr key={v.id} className="border-t border-slate-100" data-testid={`verification-${v.id}`}>
                                        <td className="px-4 py-3 text-slate-700">{v.period || "—"}</td>
                                        <td className="px-4 py-3 text-slate-700">{fmtDate(v.verification_date)}</td>
                                        <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(v.next_due_date)}</td>
                                        <td className="px-4 py-3">{resultBadge(v.result)}</td>
                                        <td className="px-4 py-3 text-slate-700">{v.sticker || "—"}</td>
                                        <td className="px-4 py-3 text-right text-slate-700">{v.cost != null ? fmtMXN(v.cost) : "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                {canWrite && (
                                                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(v)} data-testid={`edit-ver-${v.id}`}><Pencil size={14} /></button>
                                                )}
                                                {isAdmin && (
                                                    <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(v)} data-testid={`del-ver-${v.id}`}><Trash2 size={14} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="verification-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar verificación" : "Nueva verificación"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Periodo</Label>
                            <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="Ej. 2026-1" data-testid="ver-period" />
                        </div>
                        <div>
                            <Label className="text-xs">Resultado</Label>
                            <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                                <SelectTrigger data-testid="ver-result"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="aprobada">Aprobada</SelectItem>
                                    <SelectItem value="rechazada">Rechazada</SelectItem>
                                    <SelectItem value="exenta">Exenta</SelectItem>
                                    <SelectItem value="pendiente">Pendiente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Fecha realizada</Label>
                            <Input type="date" value={form.verification_date} onChange={(e) => setForm({ ...form, verification_date: e.target.value })} data-testid="ver-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Próximo vencimiento</Label>
                            <Input type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} data-testid="ver-due" />
                        </div>
                        <div>
                            <Label className="text-xs">Holograma</Label>
                            <Input value={form.sticker} onChange={(e) => setForm({ ...form, sticker: e.target.value })} placeholder="00, 0, 1, 2 o Exento" />
                        </div>
                        <div>
                            <Label className="text-xs">Costo (MXN)</Label>
                            <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notas</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-verification">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function ServiceLogsSection({ carId, items, suppliers, serviceTypes, reload, isAdmin, canWrite }) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const empty = {
        date: todayISO(),
        service_type: "",
        service_kind: "mano_de_obra",
        cost: "",
        mileage: "",
        supplier_id: "",
        next_service_date: "",
        next_service_mileage: "",
        notes: "",
        receipt_path: "",
    };
    const [form, setForm] = useState(empty);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            date: s.date,
            service_type: s.service_type || "",
            service_kind: s.service_kind || "mano_de_obra",
            cost: s.cost ?? "",
            mileage: s.mileage ?? "",
            supplier_id: s.supplier_id || "",
            next_service_date: s.next_service_date || "",
            next_service_mileage: s.next_service_mileage ?? "",
            notes: s.notes || "",
            receipt_path: s.receipt_path || "",
        });
        setOpen(true);
    };

    const handleReceiptUpload = async (file) => {
        if (!file) return;
        setUploadingReceipt(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/receipt", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, receipt_path: data.path }));
            toast.success("Comprobante adjuntado");
        } catch {
            toast.error("Error al subir el comprobante");
        } finally {
            setUploadingReceipt(false);
        }
    };

    const submit = async () => {
        if (!form.date || !form.service_type || form.cost === "") {
            return toast.error("Fecha, servicio y costo son obligatorios");
        }
        const payload = {
            date: form.date,
            service_type: form.service_type,
            service_kind: form.service_kind,
            cost: Number(form.cost),
            mileage: form.mileage !== "" ? Number(form.mileage) : null,
            supplier_id: form.supplier_id || null,
            next_service_date: form.next_service_date || null,
            next_service_mileage: form.next_service_mileage !== "" ? Number(form.next_service_mileage) : null,
            notes: form.notes || null,
            receipt_path: form.receipt_path || null,
        };
        try {
            if (editing) await api.put(`/service-logs/${editing.id}`, payload);
            else await api.post(`/cars/${carId}/service-logs`, payload);
            toast.success("Servicio guardado");
            setOpen(false);
            reload();
        } catch {
            toast.error("Error al guardar el servicio");
        }
    };

    const remove = async (s) => {
        if (!window.confirm(`¿Eliminar el registro de servicio del ${fmtDate(s.date)}?`)) return;
        await api.delete(`/service-logs/${s.id}`);
        toast.success("Eliminado");
        reload();
    };

    const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
    const sorted = [...items].sort((a, b) => b.date.localeCompare(a.date));
    const totalCost = items.reduce((acc, s) => acc + (s.cost || 0), 0);

    return (
        <Card className="border-slate-200 shadow-sm" data-testid="service-logs-section">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <Wrench size={18} className="text-slate-500" />
                        Bitácora de servicios mecánicos
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Historial de servicios y reparaciones realizadas al auto.</p>
                </div>
                <div className="flex items-center gap-2">
                    {items.length > 0 && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total en servicios</div>
                            <div className="text-sm font-medium text-slate-900">{fmtMXN(totalCost)}</div>
                        </div>
                    )}
                    {canWrite && (
                        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-service-log-button">
                            <Plus size={16} className="mr-1" /> Registrar servicio
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {sorted.length === 0 ? (
                    <div className="px-6 py-10 text-center text-slate-400 text-sm">Aún no hay servicios registrados.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                    <th className="text-left px-4 py-3 font-semibold">Servicio</th>
                                    <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                                    <th className="text-left px-4 py-3 font-semibold">Taller</th>
                                    <th className="text-left px-4 py-3 font-semibold">Kilometraje</th>
                                    <th className="text-left px-4 py-3 font-semibold">Próximo servicio</th>
                                    <th className="text-right px-4 py-3 font-semibold">Costo</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((s) => (
                                    <tr key={s.id} className="border-t border-slate-100" data-testid={`service-log-${s.id}`}>
                                        <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(s.date)}</td>
                                        <td className="px-4 py-3 text-slate-700">
                                            {s.service_type}
                                            {s.notes && <div className="text-xs text-slate-400 mt-0.5">{s.notes}</div>}
                                        </td>
                                        <td className="px-4 py-3" data-testid={`service-log-kind-${s.id}`}>
                                            {s.service_kind ? (
                                                <Badge className={s.service_kind === "mano_de_obra" ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-violet-100 text-violet-700 hover:bg-violet-100"}>
                                                    {s.service_kind === "mano_de_obra" ? "Mano de obra" : "Refacción"}
                                                </Badge>
                                            ) : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{s.supplier_id ? supplierName(s.supplier_id) : "—"}</td>
                                        <td className="px-4 py-3 text-slate-700">{s.mileage != null ? `${Number(s.mileage).toLocaleString("es-MX")} km` : "—"}</td>
                                        <td className="px-4 py-3 text-slate-700">
                                            {s.next_service_date ? fmtDate(s.next_service_date) : (s.next_service_mileage != null ? `${Number(s.next_service_mileage).toLocaleString("es-MX")} km` : "—")}
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(s.cost)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                {s.receipt_path && (
                                                    <a
                                                        href={withToken(`${API}/files/${s.receipt_path}`)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="p-1.5 rounded hover:bg-slate-100 inline-flex"
                                                        data-testid={`service-log-receipt-${s.id}`}
                                                    >
                                                        <FileImage size={14} className="text-slate-500" />
                                                    </a>
                                                )}
                                                {canWrite && (
                                                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(s)} data-testid={`edit-service-log-${s.id}`}><Pencil size={14} /></button>
                                                )}
                                                {isAdmin && (
                                                    <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(s)} data-testid={`del-service-log-${s.id}`}><Trash2 size={14} /></button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="service-log-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar servicio" : "Nuevo servicio mecánico"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Fecha del servicio</Label>
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="svc-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Costo (MXN)</Label>
                            <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="svc-cost" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Servicio realizado</Label>
                            <Input
                                value={form.service_type}
                                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                                placeholder="Ej. Cambio de aceite y filtro"
                                list="service-type-suggestions"
                                data-testid="svc-type"
                            />
                            <datalist id="service-type-suggestions">
                                {serviceTypes.map((t) => <option key={t} value={t} />)}
                            </datalist>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Tipo</Label>
                            <Select value={form.service_kind} onValueChange={(v) => setForm({ ...form, service_kind: v })}>
                                <SelectTrigger data-testid="svc-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="mano_de_obra">Mano de obra</SelectItem>
                                    <SelectItem value="refaccion">Refacción</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Taller / proveedor</Label>
                            <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="svc-supplier"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin especificar</SelectItem>
                                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Kilometraje</Label>
                            <Input type="number" min="0" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} placeholder="Ej. 42000" data-testid="svc-mileage" />
                        </div>
                        <div>
                            <Label className="text-xs">Próximo servicio (fecha)</Label>
                            <Input type="date" value={form.next_service_date} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} data-testid="svc-next-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Próximo servicio (km)</Label>
                            <Input type="number" min="0" value={form.next_service_mileage} onChange={(e) => setForm({ ...form, next_service_mileage: e.target.value })} placeholder="Ej. 47000" data-testid="svc-next-mileage" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notas</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Detalles del servicio, refacciones usadas, etc." data-testid="svc-notes" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Comprobante (opcional)</Label>
                            {form.receipt_path ? (
                                <div className="flex items-center gap-2 mt-1">
                                    <a href={withToken(`${API}/files/${form.receipt_path}`)} target="_blank" rel="noreferrer" className="text-xs text-slate-600 hover:text-slate-900 underline">
                                        Ver comprobante
                                    </a>
                                    <button type="button" onClick={() => setForm((f) => ({ ...f, receipt_path: "" }))} className="p-1 rounded hover:bg-rose-50 text-rose-600" data-testid="remove-svc-receipt">
                                        <X size={12} />
                                    </button>
                                </div>
                            ) : (
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => handleReceiptUpload(e.target.files?.[0])}
                                    disabled={uploadingReceipt}
                                    className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 disabled:opacity-50"
                                    data-testid="svc-receipt-input"
                                />
                            )}
                            {uploadingReceipt && <p className="text-xs text-slate-500 mt-1">Subiendo…</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-service-log">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

const CHECKLIST_STATUS_OPTIONS = [
    { value: "bien", label: "Bien", cls: "bg-emerald-100 text-emerald-700" },
    { value: "regular", label: "Regular", cls: "bg-amber-100 text-amber-700" },
    { value: "mal", label: "Mal", cls: "bg-rose-100 text-rose-700" },
    { value: "na", label: "N/A", cls: "bg-slate-100 text-slate-600" },
];

function checklistStatusMeta(v) {
    return CHECKLIST_STATUS_OPTIONS.find((s) => s.value === v) || CHECKLIST_STATUS_OPTIONS[3];
}

function checklistOverallStatus(items) {
    if (items.some((i) => i.status === "mal")) return { label: "Requiere atención", cls: "bg-rose-100 text-rose-700" };
    if (items.some((i) => i.status === "regular")) return { label: "Con observaciones", cls: "bg-amber-100 text-amber-700" };
    return { label: "Todo en orden", cls: "bg-emerald-100 text-emerald-700" };
}

function ChecklistSection({ carId, items, template, reload, isAdmin, canWrite }) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [date, setDate] = useState(todayISO());
    const [mileage, setMileage] = useState("");
    const [generalNotes, setGeneralNotes] = useState("");
    const [itemStates, setItemStates] = useState({});
    const [photos, setPhotos] = useState([]);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [saving, setSaving] = useState(false);

    const resetForm = (source) => {
        const map = {};
        template.forEach((t) => {
            const existing = source?.items?.find((i) => i.key === t.key);
            map[t.key] = { status: existing?.status || "bien", notes: existing?.notes || "", photos: existing?.photos || [] };
        });
        setItemStates(map);
        setDate(source?.date || todayISO());
        setMileage(source?.mileage ?? "");
        setGeneralNotes(source?.general_notes || "");
        setPhotos(source?.photos || []);
    };

    const openNew = () => { setEditing(null); resetForm(null); setOpen(true); };
    const openEdit = (c) => { setEditing(c); resetForm(c); setOpen(true); };

    const setItemStatus = (key, status) => setItemStates((s) => ({ ...s, [key]: { ...s[key], status } }));
    const setItemNotes = (key, notes) => setItemStates((s) => ({ ...s, [key]: { ...s[key], notes } }));

    const handlePhotoUpload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        setUploadingPhoto(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads/checklist-photo", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setPhotos((p) => [...p, data.path]);
            }
            toast.success(files.length > 1 ? "Fotos agregadas" : "Foto agregada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir la foto");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const removePhoto = (path) => setPhotos((p) => p.filter((x) => x !== path));

    const submit = async () => {
        if (!date) return toast.error("La fecha es obligatoria");
        const payload = {
            date,
            mileage: mileage !== "" ? Number(mileage) : null,
            general_notes: generalNotes || null,
            photos,
            items: template.map((t) => ({
                key: t.key,
                label: t.label,
                category: t.category,
                status: itemStates[t.key]?.status || "bien",
                notes: itemStates[t.key]?.notes || null,
                photos: itemStates[t.key]?.photos || [],
            })),
        };
        setSaving(true);
        try {
            if (editing) await api.put(`/checklists/${editing.id}`, payload);
            else await api.post(`/cars/${carId}/checklists`, payload);
            toast.success("Revisión guardada");
            setOpen(false);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar la revisión");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`¿Eliminar la revisión del ${fmtDate(c.date)}?`)) return;
        await api.delete(`/checklists/${c.id}`);
        toast.success("Eliminada");
        reload();
    };

    const fisicos = template.filter((t) => t.category === "fisico");
    const mecanicos = template.filter((t) => t.category === "mecanico");
    const sorted = [...items].sort((a, b) => b.date.localeCompare(a.date));
    const latest = sorted[0];
    const latestStatus = latest ? checklistOverallStatus(latest.items) : null;

    return (
        <Card className="border-slate-200 shadow-sm" data-testid="checklist-section">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <ClipboardList size={18} className="text-slate-500" />
                        Revisión (físico y mecánico)
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Checklist editable con historial de revisiones del estado del auto.</p>
                </div>
                <div className="flex items-center gap-2">
                    {latest && latestStatus && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">Última revisión</div>
                            <div className="text-sm font-medium text-slate-900">{fmtDate(latest.date)}</div>
                            <Badge className={`${latestStatus.cls} hover:${latestStatus.cls} mt-1`}>{latestStatus.label}</Badge>
                        </div>
                    )}
                    {canWrite && (
                        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-checklist-button">
                            <Plus size={16} className="mr-1" /> Nueva revisión
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                {sorted.length === 0 ? (
                    <div className="px-6 py-10 text-center text-slate-400 text-sm">Aún no hay revisiones registradas.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                    <th className="text-left px-4 py-3 font-semibold">Kilometraje</th>
                                    <th className="text-left px-4 py-3 font-semibold">Estado general</th>
                                    <th className="text-left px-4 py-3 font-semibold">Fotos</th>
                                    <th className="text-left px-4 py-3 font-semibold">Registrado por</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((c) => {
                                    const st = checklistOverallStatus(c.items);
                                    return (
                                        <tr key={c.id} className="border-t border-slate-100" data-testid={`checklist-${c.id}`}>
                                            <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(c.date)}</td>
                                            <td className="px-4 py-3 text-slate-700">{c.mileage != null ? `${Number(c.mileage).toLocaleString("es-MX")} km` : "—"}</td>
                                            <td className="px-4 py-3"><Badge className={`${st.cls} hover:${st.cls}`}>{st.label}</Badge></td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {(() => {
                                                    const totalPhotos = (c.photos?.length || 0) + (c.items || []).reduce((sum, i) => sum + (i.photos?.length || 0), 0);
                                                    return totalPhotos > 0 ? (
                                                        <span className="inline-flex items-center gap-1"><Camera size={13} /> {totalPhotos}</span>
                                                    ) : "—";
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {c.created_by || "—"}
                                                {c.is_public_submission && (
                                                    <Badge className="ml-1.5 bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px] px-1.5 py-0" data-testid={`checklist-public-badge-${c.id}`}>
                                                        Enviado por conductor
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => setViewing(c)} data-testid={`view-checklist-${c.id}`}><ClipboardCheck size={14} /></button>
                                                    <a
                                                        href={withToken(`${API}/checklists/${c.id}/export/pdf`)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="p-1.5 rounded hover:bg-slate-100 inline-flex"
                                                        data-testid={`export-checklist-pdf-${c.id}`}
                                                    >
                                                        <FileText size={14} />
                                                    </a>
                                                    {canWrite && (
                                                        <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(c)} data-testid={`edit-checklist-${c.id}`}><Pencil size={14} /></button>
                                                    )}
                                                    {isAdmin && (
                                                        <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(c)} data-testid={`del-checklist-${c.id}`}><Trash2 size={14} /></button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="checklist-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar revisión" : "Nueva revisión"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Fecha de revisión</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="checklist-date" />
                            </div>
                            <div>
                                <Label className="text-xs flex items-center gap-1"><Gauge size={12} /> Kilometraje</Label>
                                <Input type="number" min="0" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="Ej. 45230" data-testid="checklist-mileage" />
                            </div>
                        </div>

                        <ChecklistGroup title="Estado físico" items={fisicos} states={itemStates} setStatus={setItemStatus} setNotes={setItemNotes} />
                        <ChecklistGroup title="Estado mecánico" items={mecanicos} states={itemStates} setStatus={setItemStatus} setNotes={setItemNotes} />

                        <div>
                            <Label className="text-xs">Notas generales</Label>
                            <Textarea rows={2} value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} placeholder="Observaciones adicionales de la revisión" data-testid="checklist-general-notes" />
                        </div>

                        <div>
                            <Label className="text-xs flex items-center gap-1"><Camera size={12} /> Fotografías</Label>
                            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">Adjunta evidencia fotográfica del estado del auto (JPG, PNG, WEBP · máx. 5 MB c/u).</p>
                            {photos.length > 0 && (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
                                    {photos.map((p) => (
                                        <div key={p} className="relative group rounded-md overflow-hidden border border-slate-200" data-testid={`checklist-photo-${p.split("/").pop()}`}>
                                            <img src={withToken(`${API}/files/${p}`)} alt="Foto de revisión" className="w-full h-20 object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => removePhoto(p)}
                                                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                                data-testid={`remove-checklist-photo-${p.split("/").pop()}`}
                                            >
                                                <X size={11} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                onChange={(e) => { handlePhotoUpload(e.target.files); e.target.value = ""; }}
                                disabled={uploadingPhoto}
                                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 disabled:opacity-50"
                                data-testid="checklist-photo-input"
                            />
                            {uploadingPhoto && <p className="text-xs text-slate-500 mt-1">Subiendo…</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-checklist">
                            {saving ? "Guardando…" : "Guardar revisión"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="checklist-view-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Revisión del {viewing && fmtDate(viewing.date)}</DialogTitle>
                    </DialogHeader>
                    {viewing && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-sm text-slate-600">
                                <span className="flex items-center gap-1"><Gauge size={14} /> {viewing.mileage != null ? `${Number(viewing.mileage).toLocaleString("es-MX")} km` : "Sin kilometraje"}</span>
                                <span>·</span>
                                <span>Registró: {viewing.created_by || "—"}</span>
                                {viewing.is_public_submission && (
                                    <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px] px-1.5 py-0" data-testid="checklist-view-public-badge">
                                        Enviado por conductor
                                    </Badge>
                                )}
                            </div>
                            {["fisico", "mecanico"].map((cat) => (
                                <div key={cat}>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                        {cat === "fisico" ? "Estado físico" : "Estado mecánico"}
                                    </div>
                                    <div className="space-y-1.5">
                                        {viewing.items.filter((i) => i.category === cat).map((i) => {
                                            const meta = checklistStatusMeta(i.status);
                                            return (
                                                <div key={i.key} className="flex items-start justify-between gap-3 p-2 rounded-md border border-slate-100">
                                                    <div className="flex-1">
                                                        <div className="text-sm text-slate-800">{i.label}</div>
                                                        {i.notes && <div className="text-xs text-slate-500 mt-0.5">{i.notes}</div>}
                                                        {i.photos?.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                {i.photos.map((p) => (
                                                                    <a
                                                                        key={p}
                                                                        href={withToken(`${API}/files/${p}`)}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="rounded overflow-hidden border border-slate-200 hover:border-slate-400 transition-colors"
                                                                        data-testid={`view-checklist-item-photo-${p.split("/").pop()}`}
                                                                    >
                                                                        <img src={withToken(`${API}/files/${p}`)} alt="" className="w-10 h-10 object-cover" />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Badge className={`${meta.cls} hover:${meta.cls} shrink-0`}>{meta.label}</Badge>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            {viewing.general_notes && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Notas generales</div>
                                    <p className="text-sm text-slate-700">{viewing.general_notes}</p>
                                </div>
                            )}
                            {viewing.photos?.length > 0 && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                                        <Camera size={12} /> Fotografías
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                        {viewing.photos.map((p) => (
                                            <a
                                                key={p}
                                                href={withToken(`${API}/files/${p}`)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="rounded-md overflow-hidden border border-slate-200 hover:border-slate-400 transition-colors"
                                                data-testid={`view-checklist-photo-${p.split("/").pop()}`}
                                            >
                                                <img src={withToken(`${API}/files/${p}`)} alt="Foto de revisión" className="w-full h-20 object-cover" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setViewing(null)}>Cerrar</Button>
                        {viewing && (
                            <a href={withToken(`${API}/checklists/${viewing.id}/export/pdf`)} target="_blank" rel="noreferrer" data-testid="export-checklist-pdf-view">
                                <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                                    <FileText size={14} className="mr-1" /> Exportar PDF
                                </Button>
                            </a>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function ChecklistGroup({ title, items, states, setStatus, setNotes }) {
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</div>
            <div className="space-y-2">
                {items.map((t) => {
                    const state = states[t.key] || { status: "bien", notes: "" };
                    return (
                        <div key={t.key} className="p-3 rounded-md border border-slate-200 bg-white" data-testid={`checklist-item-${t.key}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="text-sm text-slate-800 font-medium">{t.label}</div>
                                <div className="flex gap-1">
                                    {CHECKLIST_STATUS_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setStatus(t.key, opt.value)}
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                                state.status === opt.value
                                                    ? `${opt.cls} border-transparent`
                                                    : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                                            }`}
                                            data-testid={`checklist-item-${t.key}-${opt.value}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <Input
                                value={state.notes}
                                onChange={(e) => setNotes(t.key, e.target.value)}
                                placeholder="Notas de este apartado (opcional)"
                                className="mt-2 text-xs h-8"
                                data-testid={`checklist-item-${t.key}-notes`}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}



function Kpi({ label, value, icon, accent, onEdit }) {
    const bar = { emerald: "bg-emerald-500", rose: "bg-rose-500", slate: "bg-slate-700" }[accent];
    return (
        <Card className="kpi-card border-slate-200 shadow-sm overflow-hidden">
            <div className={`h-1 ${bar}`} />
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{label}</div>
                    <div className="flex items-center gap-1.5">
                        {onEdit && (
                            <button
                                onClick={onEdit}
                                className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                                data-testid="edit-car-value-button"
                            >
                                <Pencil size={13} />
                            </button>
                        )}
                        {icon}
                    </div>
                </div>
                <div className="text-3xl font-semibold tracking-tighter text-slate-900 mt-3 font-display">{value}</div>
            </CardContent>
        </Card>
    );
}
